import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import htmlLegendPlugin from '../plugins/html-legend.plugin';

// Slices below this share get no label inside the pie (the legend shows all values)
const MIN_LABEL_SHARE = 0.05;

const percentFormatter = new Intl.NumberFormat('de-DE', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/**
 * Share of a single value in the dataset (0..1). Hidden slices still count,
 * so the numbers stay stable when legend items are toggled.
 */
const getShare = (data, index) => {
  const total = data.reduce((sum, n) => sum + Number(n), 0);
  return total ? Number(data[index]) / total : 0;
};

export default class PollResultModule {
  constructor (element) {
    this.element = element;
    this.chart = null;

    // cache DOM
    this.data = JSON.parse(this.element.dataset.pollResult);
    this.container = this.element.querySelector('.poll-result-legend');
    this.canvas = this.element.querySelector('.poll-result-canvas');

    // bind once so we can remove it later if needed
    this.handleShown = this.handleShown.bind(this);

    this.init();
  }

  init () {
    if (this.isVisible(this.canvas)) {
      this.createChart();
    } else {
      // wait for the nearest collapsed parent (accordion pane) to open
      const collapse = this.element.closest('.collapse');
      if (collapse) {
        collapse.addEventListener('shown.bs.collapse', this.handleShown, { once: true });
      } else {
        // Fallback: create when the canvas first becomes visible by any means
        const io = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.createChart();
              io.disconnect();
            }
          });
        }, { threshold: 0.01 });
        io.observe(this.canvas);
      }
    }
  }

  handleShown () {
    if (!this.chart) {
      this.createChart();
    } else {
      this.chart.resize();
      this.chart.update();
    }
  }

  isVisible (el) {
    // robust visibility check for hidden/collapsed containers
    const rect = el.getBoundingClientRect();
    return !!(rect.width || rect.height) && window.getComputedStyle(el).display !== 'none';
  }

  createChart () {
    this.chart = new Chart(
      this.canvas,
      {
        type: 'pie',
        data: {
          labels: this.data.map(row => row.answer),
          datasets: [
            {
              data: this.data.map(row => parseInt(row.answer_count, 10)),
              // No slice separators when only one answer has votes (otherwise a white line is drawn from the center)
              borderWidth: (ctx) => (
                ctx.dataset.data.filter(n => Number(n) > 0).length > 1 ? 2 : 0
              ),
            },
          ],
        },
        options: {
          responsive: true,
          // For hidden containers with a fixed-height parent, this helps:
          // maintainAspectRatio: false,
          plugins: {
            htmlLegend: {
              container: this.container,
              formatValue: (chart, index) => percentFormatter.format(
                getShare(chart.data.datasets[0].data, index),
              ),
            },
            legend: {
              display: false,
            },
            datalabels: {
              // Small slices: no label (they overlap). 'auto' also hides labels that would still collide.
              display: (ctx) => (
                getShare(ctx.dataset.data, ctx.dataIndex) >= MIN_LABEL_SHARE ? 'auto' : false
              ),
              formatter: (value, ctx) => percentFormatter.format(
                getShare(ctx.dataset.data, ctx.dataIndex),
              ),
              // Place labels near the outer edge where slices are wider
              anchor: 'end',
              align: 'start',
              offset: 12,
              color: '#ffffff',
              font: {
                size: 14,
                weight: 700,
              },
            },
          },
        },
        plugins: [ChartDataLabels, htmlLegendPlugin],
      },
    );
  }

  // optional: call this if the accordion pane might change size after opening
  resize () {
    if (this.chart) this.chart.resize();
  }

  // optional: clean up if the component is removed
  destroy () {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}